import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { usePrincipal } from '@/core/auth/appToken';

interface RequireOnboardingCompleteProps {
  readonly children: ReactNode;
}

/** Route the SPA must stay on/return to while onboarding is incomplete (mirrors `LoginForm`). */
const ONBOARDING_PATH = '/onboarding';

/**
 * Route guard keeping a principal with `principal.onboardingRequired` on `/onboarding`
 * regardless of how they got there — deep-link, full-page reload, or history navigation, not
 * only the post-login redirect handled by `LoginForm` (US-E10-06 AK-6). Wraps the business route
 * tree under `/`. `/onboarding` is its own top-level route (outside this guard's subtree), so the
 * current-location exemption below is no longer reachable in practice; it is kept as a defensive
 * guard in case `/onboarding` is ever nested back under `/`.
 *
 * A principal that has not (yet) resolved is passed through unchanged — `RequireAuth`, which
 * always wraps this guard, is responsible for the unauthenticated case.
 */
export function RequireOnboardingComplete({ children }: RequireOnboardingCompleteProps) {
  const principal = usePrincipal();
  const location = useLocation();

  if (principal?.onboardingRequired && location.pathname !== ONBOARDING_PATH) {
    return <Navigate to={ONBOARDING_PATH} replace />;
  }

  return <>{children}</>;
}
